import React, { useEffect, useReducer, useState } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import LoadingOverlay from "react-loading-overlay"
import Select from "react-select"
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  UncontrolledAlert,
} from "reactstrap"
import Success from "pages/NotificationModal/Success"
import { get, post, put } from "utils/apiClient"

// ─── Dynamic fields for appt system specific config ──────────────────────────

const DynamicChildForm = ({ formFields, setFieldValue, parentValues }) => (
  <div>
    <h5>Appointment System specific fields</h5>
    {formFields.map(field => (
      <Row key={field.name} className="mb-4">
        <Label className="col-sm-3 col-form-label">{field.display_name} *</Label>
        <Col sm={6}>
          {field.type === "string" && (
            <Input
              type="text"
              id={field.name}
              name={field.name}
              value={parentValues.additional_data?.[field.name] ?? ""}
              onChange={e => setFieldValue(`additional_data.${field.name}`, e.target.value)}
              placeholder={`Enter ${field.name}`}
            />
          )}
          {field.type === "integer" && (
            <Input
              type="number"
              id={field.name}
              name={field.name}
              value={parentValues.additional_data?.[field.name] ?? ""}
              onChange={e => setFieldValue(`additional_data.${field.name}`, e.target.value)}
              placeholder={`Enter ${field.name}`}
            />
          )}
          {field.type === "boolean" && (
            <input
              type="checkbox"
              className="form-check-input"
              id={field.name}
              name={field.name}
              checked={parentValues.additional_data?.[field.name] ?? false}
              onChange={() =>
                setFieldValue(
                  `additional_data.${field.name}`,
                  !(parentValues.additional_data?.[field.name] ?? false)
                )
              }
            />
          )}
        </Col>
      </Row>
    ))}
  </div>
)

// ─── Reducer ─────────────────────────────────────────────────────────────────

const initialState = { loading: false, showSuccess: false }

function reducer(state, action) {
  switch (action.type) {
    case "togLoading": return { ...state, loading: !state.loading }
    case "togSuccess": return { ...state, showSuccess: !state.showSuccess }
    default: return state
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

function MapLocationModal({ isOpen, toggle, stopData, errDetails }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [errorMessage, setErrorMessage] = useState(null)

  const [checkStatus, setCheckStatus] = useState(null)
  const [locationOptions, setLocationOptions] = useState([])
  const [selectedQdLocation, setSelectedQdLocation] = useState(null)

  // Scenario AME_NO_LOC_FOUND state
  const [selectedApptSys, setSelectedApptSys] = useState(null)
  const [allApptSys, setAllApptSys] = useState([])
  const [apptSysFields, setApptSysFields] = useState(null)
  const [apptSysWithFields, setApptSysWithFields] = useState(null)

  // ── On open: check if already mapped, then derive scenario from errDetails ─

  useEffect(() => {
    if (!isOpen || !errDetails) return
    setCheckStatus(null)
    setErrorMessage(null)
    setSelectedApptSys(null)
    setApptSysFields(null)
    setSelectedQdLocation(null)
    setLocationOptions([])
    runAlreadyMappedCheck()
  }, [isOpen])

  const runAlreadyMappedCheck = async () => {
    dispatch({ type: "togLoading" })
    try {
      const tLocId = stopData?.tmsLocationId
      const address = stopData?.location?.address || stopData?.location?.tmsStopAddress?.address || ""
      const city = stopData?.location?.city || stopData?.location?.tmsStopAddress?.city || ""
      const st = stopData?.location?.state || stopData?.location?.tmsStopAddress?.state || ""
      const zip = stopData?.location?.zip || stopData?.location?.tmsStopAddress?.zipCode || ""
      const params = new URLSearchParams({ t_loc_id: tLocId, address, city, state: st, zip })
      const result = await get(`location-management/location-mapping?${params}`)

      if (result.status === "ALREADY_MAPPED") {
        setCheckStatus("ALREADY_MAPPED")
        return
      }

      const code = errDetails.error_code
      setCheckStatus(code)
      if (code === "AME_NO_LOC_FOUND") {
        await loadAllApptSys()
      } else {
        const ids = errDetails.nearby_locations || errDetails.matched_locations || []
        await loadLocationDetails(ids)
      }
    } catch (e) {
      setErrorMessage("Failed to load location mapping status.")
    } finally {
      dispatch({ type: "togLoading" })
    }
  }

  // ── Location details loader (nearby / multi scenarios) ────────────────────

  const loadLocationDetails = async ids => {
    try {
      const result = await get(`location-management/master-locations?m_loc_ids=${ids.join(",")}`)
      setLocationOptions(result || [])
    } catch (e) {
      setErrorMessage("Failed to load location details.")
    }
  }

  // ── Appt system loader (AME_NO_LOC_FOUND scenario) ────────────────────────

  const loadAllApptSys = async () => {
    try {
      const response = await get("appt-sys/all")
      setApptSysWithFields(response)
      setAllApptSys(response ? response.map(item => ({ value: item.key, label: item.key })) : [])
    } catch (e) {
      console.error(e)
    }
  }

  const handleApptSysChange = selectedOption => {
    validation.setFieldValue("appt_system_type", selectedOption)
    setSelectedApptSys(selectedOption)
    if (selectedOption) {
      const match = apptSysWithFields.find(item => item.key === selectedOption.value)
      setApptSysFields(match?.fields || null)
    } else {
      setApptSysFields(null)
      validation.setFieldValue("additional_data", {})
    }
  }

  useEffect(() => {
    if (!apptSysFields) return
    const defaults = apptSysFields.reduce((acc, field) => {
      acc[field.name] = field.type === "boolean" ? false : ""
      return acc
    }, {})
    validation.setFieldValue("additional_data", defaults)
  }, [apptSysFields])

  // ── Save: link to selected location ───────────────────────────────────────

  const saveLink = async () => {
    if (!selectedQdLocation) return
    dispatch({ type: "togLoading" })
    setErrorMessage(null)
    try {
      await put("location-management/location-mapping", {
        t_loc_id: stopData?.tmsLocationId,
        t_loc_nm: stopData?.tmsLocationName || "",
        addr1: stopData?.location?.address || stopData?.location?.tmsStopAddress?.address || "",
        city: stopData?.location?.city || stopData?.location?.tmsStopAddress?.city || "",
        state: stopData?.location?.state || stopData?.location?.tmsStopAddress?.state || "",
        zip: stopData?.location?.zip || stopData?.location?.tmsStopAddress?.zipCode || "",
        qd_location_id: selectedQdLocation.id,
      })
      dispatch({ type: "togLoading" })
      dispatch({ type: "togSuccess" })
    } catch (e) {
      setErrorMessage(e.message)
      dispatch({ type: "togLoading" })
    }
  }

  // ── Save: create new location (AME_NO_LOC_FOUND) ──────────────────────────

  const saveCreate = async values => {
    dispatch({ type: "togLoading" })
    setErrorMessage(null)
    try {
      let addData = { ...(values.additional_data || {}) }
      apptSysFields?.forEach(field => {
        if (field.type === "integer") addData[field.name] = +addData[field.name]
      })

      const apptSysType = values.appt_system_type.value

      const linkPayload = {
        t_loc_id: stopData?.tmsLocationId,
        t_loc_nm: stopData?.tmsLocationName || values.m_loc_name,
        addr1: values.addr1,
        city: values.city,
        state: values.state,
        zip: values.zip,
        appt_sys_type: apptSysType,
      }

      try {
        await post("location-management/location-mapping", linkPayload)
      } catch (e) {
        if (!e.message.includes("404")) throw e
        await post("location-management/master-locations", {
          locationName: values.m_loc_name,
          addr1: values.addr1,
          city: values.city,
          state: values.state,
          zip: values.zip,
          appt_sys_type: apptSysType,
          additional_data: addData,
        })
        await post("location-management/location-mapping", linkPayload)
      }

      dispatch({ type: "togLoading" })
      dispatch({ type: "togSuccess" })
    } catch (e) {
      setErrorMessage(e.message)
      dispatch({ type: "togLoading" })
    }
  }

  // ── Formik (AME_NO_LOC_FOUND) ─────────────────────────────────────────────

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      m_loc_name: stopData?.tmsLocationName || "",
      addr1: stopData?.location?.address || stopData?.location?.tmsStopAddress?.address || "",
      city: stopData?.location?.city || stopData?.location?.tmsStopAddress?.city || "",
      state: stopData?.location?.state || stopData?.location?.tmsStopAddress?.state || "",
      zip: stopData?.location?.zip || stopData?.location?.tmsStopAddress?.zipCode || "",
      appt_system_type: selectedApptSys,
      additional_data: {},
    },
    validationSchema: Yup.object({
      m_loc_name: Yup.string().required("Please enter location name"),
      addr1: Yup.string().required("Please enter address"),
      appt_system_type: Yup.object()
        .shape({ value: Yup.string().required(), label: Yup.string().required() })
        .nullable()
        .required("Please select appointment system type"),
    }),
    onSubmit: values => saveCreate(values),
  })

  const closeSuccess = () => {
    dispatch({ type: "togSuccess" })
    validation.resetForm()
    setApptSysFields(null)
    toggle()
  }

  const selectStyles = {
    control: (base, _) => ({ ...base }),
    menuPortal: base => ({ ...base, zIndex: 99999 }),
    menu: base => ({ ...base, zIndex: 99999, backgroundColor: 'white', position: 'absolute' }),
    menuList: base => ({ ...base, backgroundColor: 'white' }),
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderChecking = () => (
    <div className="text-center p-4">Loading...</div>
  )

  const renderAlreadyMapped = () => (
    <div>
      <p className="text-muted mb-3">This stop's TMS location has already been mapped to a master location with an associated appointment system.</p>
      <div className="modal-footer">
        <Button color="secondary" onClick={toggle}>Close</Button>
      </div>
    </div>
  )

  const renderLocationSelection = () => (
    <div>
      <p className="text-muted mb-3">
        {checkStatus === "AME_NEARBY_LOC_FOUND"
          ? "Nearby locations were found. Select the correct one to link to this stop."
          : "Multiple locations were found. Select the correct one to link to this stop."}
      </p>
      {errorMessage && <UncontrolledAlert color="danger">{errorMessage}</UncontrolledAlert>}
      <div style={{ overflowX: "auto" }}>
        <table className="table table-bordered table-sm table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Name</th>
              <th>Address</th>
              <th>City</th>
              <th>State</th>
              <th>Country</th>
              <th>Zip</th>
              <th>Appt System</th>
            </tr>
          </thead>
          <tbody>
            {locationOptions.map(loc => (
              <tr
                key={loc.id}
                onClick={() => setSelectedQdLocation(loc)}
                style={{ cursor: "pointer" }}
                className={selectedQdLocation?.id === loc.id ? "table-primary" : ""}
              >
                <td className="text-center">
                  <input
                    type="radio"
                    checked={selectedQdLocation?.id === loc.id}
                    onChange={() => setSelectedQdLocation(loc)}
                  />
                </td>
                <td>{loc.name}</td>
                <td>{loc.address}</td>
                <td>{loc.city}</td>
                <td>{loc.state}</td>
                <td>{loc.country}</td>
                <td>{loc.zip}</td>
                <td>{loc.apptSystem}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="modal-footer">
        <Button color="primary" onClick={saveLink} disabled={!selectedQdLocation}>Save</Button>
        <Button color="secondary" onClick={toggle}>Cancel</Button>
      </div>
    </div>
  )

  const renderNoLocFound = () => (
    <Form onSubmit={validation.handleSubmit} className="p-2">
      {errorMessage && <UncontrolledAlert color="danger">{errorMessage}</UncontrolledAlert>}
      <Row className="mb-4">
        <Label className="col-sm-3 col-form-label">Appointment System Type</Label>
        <Col sm={6}>
          <Select
            name="appt_system_type"
            options={allApptSys}
            value={validation.values.appt_system_type}
            onChange={handleApptSysChange}
            onBlur={() => validation.setFieldTouched("appt_system_type", true)}
            styles={selectStyles}
            menuPortalTarget={document.body}
            isClearable
          />
          {validation.touched.appt_system_type && validation.errors.appt_system_type && (
            <div style={{ color: "red" }}>{validation.errors.appt_system_type}</div>
          )}
        </Col>
      </Row>
      <Row className="mb-4">
        <Label className="col-sm-3 col-form-label">Location Name</Label>
        <Col sm={6}>
          <Input
            type="text"
            name="m_loc_name"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.m_loc_name}
            invalid={validation.touched.m_loc_name && !!validation.errors.m_loc_name}
          />
        </Col>
      </Row>
      <Row className="mb-4">
        <Label className="col-sm-3 col-form-label">Address Line 1</Label>
        <Col sm={6}>
          <Input
            type="text"
            name="addr1"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.addr1}
            invalid={validation.touched.addr1 && !!validation.errors.addr1}
          />
        </Col>
      </Row>
      <Row className="mb-4">
        <Label className="col-sm-3 col-form-label">City</Label>
        <Col sm={6}>
          <Input type="text" name="city" onChange={validation.handleChange} onBlur={validation.handleBlur} value={validation.values.city} />
        </Col>
      </Row>
      <Row className="mb-4">
        <Label className="col-sm-3 col-form-label">State</Label>
        <Col sm={6}>
          <Input type="text" name="state" onChange={validation.handleChange} onBlur={validation.handleBlur} value={validation.values.state} />
        </Col>
      </Row>
      <Row className="mb-4">
        <Label className="col-sm-3 col-form-label">Zip</Label>
        <Col sm={6}>
          <Input type="text" name="zip" onChange={validation.handleChange} onBlur={validation.handleBlur} value={validation.values.zip} />
        </Col>
      </Row>
      {apptSysFields && apptSysFields.length > 0 && (
        <DynamicChildForm
          formFields={apptSysFields}
          setFieldValue={validation.setFieldValue}
          parentValues={validation.values}
        />
      )}
      <div className="modal-footer">
        <Button type="submit" color="primary">Save</Button>
        <Button type="button" color="secondary" onClick={toggle}>Cancel</Button>
      </div>
    </Form>
  )

  return (
    <>
      <Modal isOpen={state.showSuccess} toggle={closeSuccess} centered size="sm">
        <Success message="Location mapped successfully!" close={closeSuccess} />
      </Modal>

      <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
        <LoadingOverlay active={state.loading} spinner>
          <ModalHeader toggle={toggle}>Map Location</ModalHeader>
          <ModalBody>
            {!checkStatus && renderChecking()}
            {checkStatus === "ALREADY_MAPPED" && renderAlreadyMapped()}
            {(checkStatus === "AME_NEARBY_LOC_FOUND" || checkStatus === "AME_MULTI_QD_LOC_FOUND") && renderLocationSelection()}
            {checkStatus === "AME_NO_LOC_FOUND" && renderNoLocFound()}
          </ModalBody>
        </LoadingOverlay>
      </Modal>
    </>
  )
}

export default MapLocationModal
